import React, { useState, useEffect } from 'react';
import { ChromePicker } from 'react-color';
import { FiSave, FiUpload, FiRefreshCw, FiEye } from 'react-icons/fi';
import axios from '../config/axios';
import { useTranslation } from '../hooks/useTranslation';

const WebsiteSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get('/cms/white-label');
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put('/cms/white-label', settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (type, file) => {
    const formData = new FormData();
    formData.append(type, file);

    try {
      const response = await axios.post(`/cms/white-label/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSettings(prev => ({
        ...prev,
        [`${type}_url`]: response.data[`${type}_url`]
      }));

      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully!`);
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      alert(`Failed to upload ${type}`);
    }
  };

  const ColorPickerButton = ({ label, colorKey, value }) => (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowColorPicker(showColorPicker === colorKey ? null : colorKey)}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:border-primary-500 transition-colors"
        >
          <div
            className="w-6 h-6 rounded border border-gray-300"
            style={{ backgroundColor: value }}
          />
          <span className="text-sm font-mono">{value}</span>
        </button>
      </div>
      {showColorPicker === colorKey && (
        <div className="absolute z-10 mt-2">
          <div
            className="fixed inset-0"
            onClick={() => setShowColorPicker(null)}
          />
          <ChromePicker
            color={value}
            onChange={(color) => setSettings(prev => ({ ...prev, [colorKey]: color.hex }))}
          />
        </div>
      )}
    </div>
  );

  const FileUploadButton = ({ label, type, currentUrl }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex items-center gap-3">
        <label className="cursor-pointer">
          <div className="btn btn-primary flex items-center gap-2">
            <FiUpload className="w-4 h-4" />
            <span>Upload</span>
          </div>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              if (e.target.files[0]) {
                handleFileUpload(type, e.target.files[0]);
              }
            }}
          />
        </label>
        {currentUrl && (
          <img
            src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${currentUrl}`}
            alt={label}
            className="h-12 w-auto object-contain border border-gray-200 rounded"
          />
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FiRefreshCw className="w-8 h-8 text-primary-500 animate-spin mx-auto mb-2" />
          <p className="text-gray-600">{t('cms.common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load website settings</p>
          <button
            onClick={loadSettings}
            className="btn btn-primary"
          >
            <FiRefreshCw className="inline-block w-4 h-4 mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.websiteSettings.title')}</h1>
          <p className="text-gray-600">{t('cms.websiteSettings.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FiEye className="w-4 h-4" />
            {previewMode ? 'Hide Preview' : 'Show Preview'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <FiSave className="w-4 h-4" />
            {saving ? t('cms.common.loading') : t('cms.common.save')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Panel */}
        <div className="space-y-6">
          {/* Branding */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Branding</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site Title</label>
                <input
                  type="text"
                  value={settings.site_title || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, site_title: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tagline</label>
                <input
                  type="text"
                  value={settings.site_tagline || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, site_tagline: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <FileUploadButton label="Logo" type="logo" currentUrl={settings.logo_url} />
              <FileUploadButton label={t('cms.websiteSettings.favicon')} type="favicon" currentUrl={settings.favicon_url} />
            </div>
          </div>

          {/* Colors */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Colors</h2>
            <div className="grid grid-cols-2 gap-4">
              <ColorPickerButton label="Primary" colorKey="primary_color" value={settings.primary_color} />
              <ColorPickerButton label="Secondary" colorKey="secondary_color" value={settings.secondary_color} />
              <ColorPickerButton label="Accent" colorKey="accent_color" value={settings.accent_color} />
              <ColorPickerButton label="Background" colorKey="background_color" value={settings.background_color} />
              <ColorPickerButton label="Text" colorKey="text_color" value={settings.text_color} />
            </div>
          </div>

          {/* Typography */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Typography</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Font</label>
                <select
                  value={settings.font_primary || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, font_primary: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="Inter, system-ui, sans-serif">Inter (Sans-serif)</option>
                  <option value="Georgia, serif">Georgia (Serif)</option>
                  <option value="'Courier New', monospace">Courier New (Monospace)</option>
                  <option value="Arial, sans-serif">Arial (Sans-serif)</option>
                  <option value="'Times New Roman', serif">Times New Roman (Serif)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Heading Font</label>
                <select
                  value={settings.font_heading || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, font_heading: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="Georgia, serif">Georgia (Serif)</option>
                  <option value="Inter, system-ui, sans-serif">Inter (Sans-serif)</option>
                  <option value="'Playfair Display', serif">Playfair Display (Serif)</option>
                  <option value="'Montserrat', sans-serif">Montserrat (Sans-serif)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Base Font Size: {settings.font_size_base}px</label>
                <input
                  type="range"
                  min="12"
                  max="20"
                  value={settings.font_size_base || 16}
                  onChange={(e) => setSettings(prev => ({ ...prev, font_size_base: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Layout */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Layout</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Navigation Position</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, navigation_position: 'top' }))}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                      settings.navigation_position === 'top'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    Top
                  </button>
                  <button
                    onClick={() => setSettings(prev => ({ ...prev, navigation_position: 'left' }))}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                      settings.navigation_position === 'left'
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    Left
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Header Height: {settings.header_height}px</label>
                <input
                  type="range"
                  min="60"
                  max="120"
                  value={settings.header_height || 80}
                  onChange={(e) => setSettings(prev => ({ ...prev, header_height: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Content Width: {settings.max_content_width}px</label>
                <input
                  type="range"
                  min="1024"
                  max="1920"
                  step="64"
                  value={settings.max_content_width || 1280}
                  onChange={(e) => setSettings(prev => ({ ...prev, max_content_width: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Background */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Background</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <div className="flex gap-3">
                  {['solid', 'gradient', 'image'].map(type => (
                    <button
                      key={type}
                      onClick={() => setSettings(prev => ({ ...prev, background_type: type }))}
                      className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors capitalize ${
                        settings.background_type === type
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              {settings.background_type === 'gradient' && (
                <div className="grid grid-cols-2 gap-4">
                  <ColorPickerButton label="Gradient Start" colorKey="background_gradient_start" value={settings.background_gradient_start} />
                  <ColorPickerButton label="Gradient End" colorKey="background_gradient_end" value={settings.background_gradient_end} />
                </div>
              )}
              {settings.background_type === 'image' && (
                <FileUploadButton label="Background Image" type="background" currentUrl={settings.background_image_url} />
              )}
            </div>
          </div>

          {/* Social Media */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('cms.websiteSettings.socialMedia')}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
                <input
                  type="email"
                  value={settings.contact_email || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Facebook URL</label>
                <input
                  type="url"
                  value={settings.social_facebook || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, social_facebook: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Instagram URL</label>
                <input
                  type="url"
                  value={settings.social_instagram || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, social_instagram: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Twitter URL</label>
                <input
                  type="url"
                  value={settings.social_twitter || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, social_twitter: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Live Preview Panel */}
        {previewMode && (
          <div className="lg:sticky lg:top-6 h-fit">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Live Preview</h2>
              <div
                className="border-2 border-gray-200 rounded-lg p-8 min-h-[400px]"
                style={{
                  backgroundColor: settings.background_type === 'gradient'
                    ? `linear-gradient(to bottom, ${settings.background_gradient_start}, ${settings.background_gradient_end})`
                    : settings.background_color,
                  backgroundImage: settings.background_type === 'image' && settings.background_image_url
                    ? `url(${process.env.REACT_APP_API_URL.replace('/api', '')}${settings.background_image_url})`
                    : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  fontFamily: settings.font_primary,
                  fontSize: `${settings.font_size_base}px`,
                  color: settings.text_color
                }}
              >
                {settings.logo_url && (
                  <img
                    src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${settings.logo_url}`}
                    alt="Logo"
                    className="h-12 mb-6"
                  />
                )}
                <h1
                  className="text-4xl font-bold mb-2"
                  style={{
                    fontFamily: settings.font_heading,
                    color: settings.primary_color
                  }}
                >
                  {settings.site_title}
                </h1>
                <p className="text-lg mb-6" style={{ color: settings.text_color }}>
                  {settings.site_tagline}
                </p>
                <div className="flex gap-3 mb-6">
                  <button
                    className="px-6 py-2 rounded-lg font-medium transition-colors"
                    style={{
                      backgroundColor: settings.primary_color,
                      color: '#fff'
                    }}
                  >
                    Primary Button
                  </button>
                  <button
                    className="px-6 py-2 rounded-lg font-medium transition-colors"
                    style={{
                      backgroundColor: settings.secondary_color,
                      color: '#fff'
                    }}
                  >
                    Secondary Button
                  </button>
                </div>
                <div className="space-y-3">
                  <div
                    className="p-4 rounded-lg"
                    style={{
                      backgroundColor: `${settings.accent_color}20`,
                      borderLeft: `4px solid ${settings.accent_color}`
                    }}
                  >
                    <p style={{ color: settings.text_color }}>
                      This is a sample content block with accent color.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebsiteSettings;
